import { useCallback, useRef } from 'react';

/**
 * Detects a long-press (default 500ms) on a target element and calls `onLongPress`.
 * If the user releases before the threshold, `onClick` is called instead.
 * Cancels on pointer move beyond `moveTolerance` px, on scroll, or on cancel.
 *
 * Usage:
 *   const bind = useLongPress({ onLongPress: () => openPeek(), onClick: () => navigate() });
 *   <div {...bind}>...</div>
 */
export default function useLongPress({
  onLongPress,
  onClick,
  delay = 500,
  moveTolerance = 8,
}) {
  const timerRef = useRef(null);
  const firedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback((e) => {
    // Ignore right-click / modifier clicks — let native handlers run.
    if (e.button != null && e.button !== 0) return;
    firedRef.current = false;
    startRef.current = { x: e.clientX ?? 0, y: e.clientY ?? 0 };
    clear();
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      onLongPress?.(e);
    }, delay);
  }, [clear, delay, onLongPress]);

  const onPointerMove = useCallback((e) => {
    if (!timerRef.current) return;
    const dx = (e.clientX ?? 0) - startRef.current.x;
    const dy = (e.clientY ?? 0) - startRef.current.y;
    if (Math.hypot(dx, dy) > moveTolerance) clear();
  }, [clear, moveTolerance]);

  const onPointerUp = useCallback((e) => {
    const wasLongPress = firedRef.current;
    clear();
    if (!wasLongPress) onClick?.(e);
  }, [clear, onClick]);

  const onPointerCancel = clear;
  const onPointerLeave = clear;

  // Long-press on mobile also triggers the OS context menu — suppress it once
  // our timer has fired so the peek isn't fighting the browser.
  const onContextMenu = useCallback((e) => {
    if (firedRef.current) e.preventDefault();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
    onContextMenu,
  };
}
